import { PaginationState } from '@tanstack/react-table';

const STORAGE_PREFIX = 'table-pagination';

/**
 * Get storage key for a given pathname
 */
export const getPaginationStorageKey = (pathname: string): string => {
  return `${STORAGE_PREFIX}${pathname}`;
};

/**
 * Load pagination state from localStorage
 */
export const loadPaginationState = (pathname: string): PaginationState | null => {
  try {
    const key = getPaginationStorageKey(pathname);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as PaginationState;

    // Validate the stored data
    if (
      typeof parsed.pageIndex === 'number' &&
      typeof parsed.pageSize === 'number' &&
      parsed.pageIndex >= 0 &&
      parsed.pageSize > 0
    ) {
      return parsed;
    }

    return null;
  } catch (error) {
    console.warn('Failed to load pagination state from localStorage:', error);
    return null;
  }
};

/**
 * Save pagination state to localStorage
 */
export const savePaginationState = (pathname: string, state: PaginationState): void => {
  try {
    const key = getPaginationStorageKey(pathname);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save pagination state to localStorage:', error);
  }
};

/**
 * Clear pagination state for a specific pathname
 */
export const clearPaginationState = (pathname: string): void => {
  try {
    const key = getPaginationStorageKey(pathname);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear pagination state from localStorage:', error);
  }
};

/**
 * Clear all pagination states (used during logout)
 */
export const clearAllPaginationStates = (): void => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to clear all pagination states from localStorage:', error);
  }
};

/**
 * Get default pagination state
 * Note: Uses literal values to avoid dependency cycle with app/constants
 */
export const getDefaultPaginationState = (): PaginationState => ({
  pageIndex: 0, // FROM constant value
  pageSize: 5, // PAGE_SIZE constant value
});
